/**
 * Shared API client — adds X-User-Email header to every request
 * so the backend knows which user is making the call.
 */
const API_BASE = process.env.REACT_APP_CIMA_API_URL || 'http://localhost:3004';

// Set by App.tsx after Auth0 login
let _userEmail: string | null = null;

export function setApiUserEmail(email: string) {
  _userEmail = email;
}

export function getApiUserEmail(): string | null {
  return _userEmail;
}

export function getApiHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (_userEmail) headers['X-User-Email'] = _userEmail;
  return headers;
}

/**
 * Fire-and-forget frontend error reporter. Backend route at
 * /cima/_telemetry/error accepts an unauthenticated POST and forwards
 * to Slack via cima.notify. We catch (await) the fetch to avoid an
 * uncaught-promise-rejection that itself triggers another error report
 * (which would recurse). On any failure we silently drop — the user's
 * visible error UI doesn't change.
 *
 * In-memory dedup: don't post the same message twice within 30s. The
 * window.onerror listener fires multiple times for the same root cause
 * during a render loop crash, and we don't want to spam Slack.
 */
const _recentErrors = new Map<string, number>();
const ERROR_DEDUP_MS = 30 * 1000;

export async function reportFrontendError(
  message: string,
  opts: { stack?: string; source?: string; url?: string } = {},
): Promise<void> {
  try {
    const now = Date.now();
    const key = `${message}|${(opts.stack || '').slice(0, 80)}`;
    const last = _recentErrors.get(key);
    if (last && now - last < ERROR_DEDUP_MS) return;
    _recentErrors.set(key, now);

    // Cap the in-memory map size so a noisy session doesn't leak memory.
    if (_recentErrors.size > 100) {
      const oldestKey = _recentErrors.keys().next().value;
      if (oldestKey !== undefined) _recentErrors.delete(oldestKey);
    }

    await fetch(`${API_BASE}/cima/_telemetry/error`, {
      method:  'POST',
      headers: getApiHeaders(),  // includes X-User-Email if logged in
      body:    JSON.stringify({
        message,
        stack:  opts.stack,
        source: opts.source || 'window.onerror',
        url:    opts.url    || window.location.href,
      }),
      // No keepalive needed — the route returns 202 within ~50ms.
      // No credentials — endpoint is intentionally unauthenticated.
    });
  } catch {
    // Silently drop. Reporting failure must never escalate.
  }
}

export { API_BASE };
